import { ReactNode } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Videocam as VideocamIcon,
  VideocamOff as VideocamOffIcon,
} from "@mui/icons-material";
import { VIDEO_BG } from "../styles";
import Flex from "../../components/Flex";
import {
  TELEHEALTH_REJOIN_LABEL,
  TELEHEALTH_LEAVE_LABEL,
} from "../telehealth-v2-constant";

interface LobbyProps {
  displayName: string;
  status: "waiting" | "denied";
  onLeave?: () => void;
  /** Denied state only: re-enter the session and knock again, so the host
   *  gets a fresh admit request. Omit to hide the action. */
  onRejoin?: () => void;
  /** Optional media controls — only rendered in the waiting state when
   *  the toggle handlers are supplied. Lets guests adjust their mic and
   *  camera before the host admits them, so they don't enter the visit
   *  unmuted by surprise (or with the wrong device active). */
  micActive?: boolean;
  videoOn?: boolean;
  onToggleAudio?: () => void;
  onToggleVideo?: () => void;
  /** Optional self-video preview. Parent passes a sized VideoStage; the
   *  Lobby owns the framing so the empty / camera-off state can be drawn
   *  without re-mounting the SDK render path. */
  videoPreview?: ReactNode;
  /** Roster of everyone else currently in the Zoom session, used to tell
   *  the guest who's already in the visit. The parent excludes self before
   *  passing. */
  roomMembers?: string[];
}

const PREVIEW_W = 360;
const PREVIEW_H = 240;

export default function Lobby({
  displayName,
  status,
  onLeave,
  onRejoin,
  micActive,
  videoOn,
  onToggleAudio,
  onToggleVideo,
  videoPreview,
  roomMembers,
}: Readonly<LobbyProps>) {
  const showMediaControls =
    status === "waiting" && (onToggleAudio || onToggleVideo);
  const memberLine = formatRoomMembers(roomMembers);

  if (status === "denied") {
    return (
      <Stack
        sx={{
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          bgcolor: "background.paper",
          color: "text.primary",
          p: 4,
          textAlign: "center",
        }}
      >
        <Typography variant="h5" gutterBottom>
          The host has mistakenly declined your request to join.
        </Typography>
        <Typography
          sx={{
            color: "text.secondary",
          }}
        >
          You can ask to join again, or contact the practice if you believe this
          was a mistake.
        </Typography>
        <Stack
          direction="row"
          sx={{
            gap: 1.5,
          }}
        >
          {onLeave && (
            <Button variant="outlined" onClick={onLeave}>
              {TELEHEALTH_LEAVE_LABEL}
            </Button>
          )}
          {onRejoin && (
            <Button variant="contained" onClick={onRejoin}>
              {TELEHEALTH_REJOIN_LABEL}
            </Button>
          )}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      sx={{
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        bgcolor: "background.paper",
        color: "text.primary",
        p: 4,
        flexWrap: "wrap",
      }}
    >
      {/* LEFT — preview. Box stays mounted regardless of camera state so
          turning video off doesn't tear down the SDK render binding. The
          camera-off placeholder draws on top of the (paused) video element
          via opacity. */}
      <Box
        sx={{
          width: PREVIEW_W,
          height: PREVIEW_H,
          position: "relative",
          borderRadius: 1.5,
          overflow: "hidden",
          bgcolor: VIDEO_BG,
          border: 1,
          borderColor: "divider",
          flexShrink: 0,
          display: "flex",
        }}
      >
        {videoPreview}
        {!videoOn && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.25,
              bgcolor: VIDEO_BG,
              color: "common.white",
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 28,
                fontWeight: 500,
              }}
            >
              {displayName?.[0]?.toUpperCase() ?? "?"}
            </Box>
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                gap: 0.75,
                opacity: 0.7,
              }}
            >
              <VideocamOffIcon fontSize="small" />
              <Typography
                sx={{
                  fontSize: 13,
                }}
              >
                Camera off
              </Typography>
            </Stack>
          </Box>
        )}
      </Box>

      {/* RIGHT — status, controls, leave. */}
      <Stack
        sx={{
          gap: 2.5,
          alignItems: "flex-start",
          maxWidth: 360,
          textAlign: "left",
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <CircularProgress size={24} color="primary" />
          <Typography variant="h5">
            Waiting for the host to admit you
          </Typography>
        </Stack>
        <Typography
          sx={{
            color: "text.secondary",
          }}
        >
          You&apos;re in the lobby as <b>{displayName}</b>. The visit will start
          as soon as the provider admits you.
        </Typography>
        {memberLine && (
          <Typography
            sx={{
              color: "text.secondary",
              fontSize: 14,
            }}
          >
            {memberLine}
          </Typography>
        )}

        <Flex gap={3} justifyContent="flex-start" alignItems="center">
          {showMediaControls && (
            <Stack
              direction="row"
              sx={{
                gap: 1.5,
              }}
            >
              {onToggleAudio && (
                <Tooltip title={micActive ? "Mute" : "Unmute"}>
                  <IconButton
                    onClick={onToggleAudio}
                    sx={mediaButtonSx(!!micActive)}
                  >
                    {micActive ? <MicIcon /> : <MicOffIcon />}
                  </IconButton>
                </Tooltip>
              )}
              {onToggleVideo && (
                <Tooltip title={videoOn ? "Stop video" : "Start video"}>
                  <IconButton
                    onClick={onToggleVideo}
                    sx={mediaButtonSx(!!videoOn)}
                  >
                    {videoOn ? <VideocamIcon /> : <VideocamOffIcon />}
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          )}

          {onLeave && (
            <Button variant="contained" onClick={onLeave}>
              {TELEHEALTH_LEAVE_LABEL}
            </Button>
          )}
        </Flex>
      </Stack>
    </Stack>
  );
}

/** Build a "Dr. Smith is already in the visit" / "Dr. Smith and 2 others
 *  are in the visit" sentence from a list of names, with names wrapped in
 *  <b> so they stand out. Returns null when nobody else is in the room so
 *  the caller can hide the line entirely. */
function formatRoomMembers(names?: string[]): ReactNode {
  if (!names || names.length === 0) return null;
  const cleaned = names.map((n) => (n && n.trim()) || "Guest");
  if (cleaned.length === 1) {
    return (
      <>
        <b>{cleaned[0]}</b> is already in the visit.
      </>
    );
  }
  if (cleaned.length === 2) {
    return (
      <>
        <b>{cleaned[0]}</b> and <b>{cleaned[1]}</b> are already in the visit.
      </>
    );
  }
  const extra = cleaned.length - 2;
  return (
    <>
      <b>{cleaned[0]}</b>, <b>{cleaned[1]}</b> and {extra}{" "}
      {extra === 1 ? "other" : "others"} are already in the visit.
    </>
  );
}

function mediaButtonSx(active: boolean) {
  return active
    ? {
        bgcolor: "primary.main",
        color: "primary.contrastText",
        "&:hover": { bgcolor: "primary.dark" },
      }
    : {
        bgcolor: "error.main",
        color: "error.contrastText",
        "&:hover": { bgcolor: "error.dark" },
      };
}
